// The discussion as the last section of an article (docs/design/discussion-plan.md): one quiet row
// that opens in place, the comments under it, and one line for every state that has nothing to open.
// Drawn with createElement and textContent only, by the web reader and (through the shared reader
// bundle) the phone's reader page alike. Styled by packages/tokens/reader.css (`.disc`).
import { commentsLabel, discussionPublisher, safeHref, siteName, type Comment, type Run } from "./discussion";
import { formatAge } from "./time";
import { discussionState, DiscussionSession, type DiscussionReader, type DiscussionViewState } from "./discussionSession";

export interface DiscussionHost extends DiscussionReader {
  /** The discussion on the publisher's site, as far as the reader knows it (the article's own address). */
  link: string;
  /** The first page (no cursor) or the one after [cursor]; [fresh] asks the publisher again. Rejects when it cannot be reached. */
  now?: () => number;
}

/** What the rest of the reader needs to know: whether to show a count, a Contents entry. */
export interface DiscussionInfo { phase: "idle" | "loading" | "ready"; state?: DiscussionViewState; total?: number }

export interface DiscussionView {
  /** Begin loading (the host decides when: the article has been open a moment). Does nothing twice. */
  start(): void;
  refresh(): void;
  /** Open the row in place (once there is a discussion to open). */
  expand(): void;
  info(): DiscussionInfo;
  pause(): void;
  disable(): void;
  dispose(): void;
}

const SVG = "http://www.w3.org/2000/svg";
const COMMENT = "M6 4.5h12A2.5 2.5 0 0 1 20.5 7v7.5a2.5 2.5 0 0 1-2.5 2.5h-6l-4.5 3.5V17H6a2.5 2.5 0 0 1-2.5-2.5V7A2.5 2.5 0 0 1 6 4.5z";

function svgIcon(doc: Document, path: string, size: number, cls = "") {
  const s = doc.createElementNS(SVG, "svg");
  s.setAttribute("class", `i ${cls}`.trim()); s.setAttribute("width", String(size)); s.setAttribute("height", String(size)); s.setAttribute("viewBox", "0 0 24 24");
  s.setAttribute("fill", "none"); s.setAttribute("stroke", "currentColor"); s.setAttribute("stroke-width", "1.6"); s.setAttribute("stroke-linecap", "round"); s.setAttribute("stroke-linejoin", "round"); s.setAttribute("aria-hidden", "true");
  const p = doc.createElementNS(SVG, "path"); p.setAttribute("d", path); s.append(p);
  return s;
}

/** The count in an article's byline (the phone's reader page makes its own; the web's is React's): a button that jumps to the discussion. */
export function discussionChip(doc: Document, total: number, onClick: () => void): HTMLButtonElement {
  const b = doc.createElement("button");
  b.type = "button"; b.className = "r-disc";
  b.setAttribute("aria-label", `${commentsLabel(total)}: jump to the discussion`);
  const n = doc.createElement("span"); n.textContent = total.toLocaleString("en-US");
  b.append(svgIcon(doc, COMMENT, 14), n);
  b.addEventListener("click", onClick);
  return b;
}
const CHEVRON = "m6 9 6 6 6-6";
const INFO = "M12 20.5a8.5 8.5 0 1 1 0-17 8.5 8.5 0 0 1 0 17zM12 11v5.5M12 7.5h.01";
/** Longer than this (characters) a comment is clamped with "Read more". */
const LONG = 560;
let serial = 0;

/** How long ago, in words: "just now", "4 minutes ago", "2 hours ago", "3 days ago". */
export function since(ts: number, now: number): string {
  const m = Math.floor((now - ts) / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}

export function mountDiscussion(el: HTMLElement, host: DiscussionHost, onChange?: (info: DiscussionInfo) => void): DiscussionView {
  const doc = el.ownerDocument, now = () => (host.now ?? Date.now)(), id = `disc-${++serial}`;
  let expanded = false, disposed = false, shape = "";
  const opened = new Set<string>(), unwrapped = new Set<string>();
  const nodes = new Map<string, { key: string; el: HTMLElement }>();
  let door: HTMLButtonElement | null = null, wrap: HTMLElement | null = null, inner: HTMLElement | null = null;
  let more: HTMLButtonElement | null = null, foot: HTMLElement | null = null, stamp: HTMLElement | null = null, refresh: HTMLButtonElement | null = null;
  const session = new DiscussionSession(host, () => { render(); onChange?.(info()); }, host.link);
  const info = (): DiscussionInfo => {
    const s = session.snapshot();
    return { phase: s.phase, ...(s.phase === "ready" ? { state: discussionState(s), total: s.window?.total } : {}) };
  };
  const h = <K extends keyof HTMLElementTagNameMap>(tag: K, cls = "", text?: string): HTMLElementTagNameMap[K] => {
    const e = doc.createElement(tag); if (cls) e.className = cls; if (text !== undefined) e.textContent = text; return e;
  };
  const button = (cls: string, text: string, click: () => void) => { const e = h("button", cls, text); e.type = "button"; e.addEventListener("click", click); return e; };
  const icon = (path: string, size: number) => svgIcon(doc, path, size);
  const outLink = (text: string, href: string) => { const a = h("a", "", text), safe = safeHref(href); if (safe) { a.href = safe; a.target = "_blank"; a.rel = "noopener noreferrer"; } return a; };
  const runs = (into: Node, list: Run[]) => {
    for (const r of list) {
      if (typeof r === "string") into.appendChild(doc.createTextNode(r));
      else if ("code" in r) into.appendChild(h("code", "", r.code));
      else if ("i" in r || "b" in r) { const e = h("i" in r ? "em" : "strong"); runs(e, "i" in r ? r.i : r.b); into.appendChild(e); }
      else { const safe = safeHref(r.href); if (!safe) { runs(into, r.a); continue; } const a = outLink("", safe); runs(a, r.a); into.appendChild(a); }
    }
  };
  const length = (c: Comment) => c.body.reduce((n, p) => n + p.reduce((m, r) => m + (typeof r === "string" ? r.length : JSON.stringify(r).length), 0), 0);
  function comment(c: Comment, reply = false): HTMLElement {
    const art = h("article", `cm ${reply ? "reply" : "root"}`); art.dataset.id = c.id;
    if (c.removed) art.append(h("p", "cm-gone", c.hidden ? "Hidden by the site: low score" : "Comment removed"));
    else {
      const meta = h("div", "cm-meta"); meta.append(h("span", "by", c.by));
      if (c.tag) meta.append(h("span", "tag", c.tag)); if (c.to) meta.append(h("span", "to", c.to));
      if (c.at !== undefined) {
        const label = formatAge(c.at, now(), new Date(c.at).getTimezoneOffset());
        const t = h("span", "t"), time = safeHref(c.link) ? outLink(label, c.link!) : h("span", "", label);
        time.title = new Date(c.at).toLocaleString(); t.append(time); meta.append(t);
      }
      if (c.score) meta.append(h("span", "s", c.score > 0 ? `+${c.score}` : `−${Math.abs(c.score)}`));
      const body = h("div", "cm-body");
      if (c.quote) { const q = h("div", "cm-q"); if (c.quote.by) q.append(h("b", "", c.quote.by)); q.append(doc.createTextNode(c.quote.text)); body.append(q); }
      for (const paragraph of c.body) { const p = h("p"); runs(p, paragraph); body.append(p); }
      art.append(meta, body);
      if (length(c) > LONG) {
        const toggle = button("act", "Read more", () => { if (unwrapped.has(c.id)) unwrapped.delete(c.id); else unwrapped.add(c.id); set(); });
        const set = () => { const open = unwrapped.has(c.id); body.classList.toggle("clamp", !open); toggle.textContent = open ? "Show less" : "Read more"; toggle.setAttribute("aria-expanded", String(open)); };
        set(); art.append(toggle);
      }
    }
    const record = session.record(c.id), count = Math.max(record?.replyCount ?? 0, c.replies?.length ?? 0);
    if (!reply && count) {
      const toggle = button("act", "", () => { const open = !opened.has(c.id); if (open) opened.add(c.id); else opened.delete(c.id); set(); if (open && !c.replies?.length && record?.replyNext) void session.replies(c.id); });
      toggle.append(h("span", "", count === 1 ? "1 reply" : `${count} replies`), icon(CHEVRON, 12));
      const replies = h("div", "cm-replies"), clip = h("div"), contents = h("div", "in"); replies.append(clip); clip.append(contents);
      let built = false;
      let next: HTMLButtonElement | undefined;
      const set = () => {
        const open = opened.has(c.id);
        if (open && !built) { built = true; for (const r of c.replies ?? []) contents.insertBefore(comment(r, true), next ?? null); }
        replies.classList.toggle("open", open); toggle.setAttribute("aria-expanded", String(open)); replies.toggleAttribute("inert", !open);
      };
      set();
      if (record?.replyNext) {
        const s = session.snapshot(), loading = s.replyBusy.has(c.id), failed = s.replyFailed.has(c.id);
        next = button("act", loading ? "Loading…" : failed && s.failure === "changed" ? "Discussion changed. Refresh to continue" : failed ? "Couldn't load replies. Try again" : "Show more replies", async () => {
          if (session.snapshot().failure === "changed") { await session.refresh(); return; }
          await session.replies(c.id);
          const root = [...el.querySelectorAll<HTMLElement>(".cm.root")].find(n => n.dataset.id === c.id);
          const target = root?.querySelector<HTMLElement>("[data-replies]") ?? root;
          if (target) { if (target === root) target.tabIndex = -1; target.focus({ preventScroll: true }); }
        });
        next.dataset.replies = c.id;
        next.disabled = loading; contents.append(next);
      }
      art.append(toggle, replies);
    }
    return art;
  }
  function setOpen(open: boolean) { expanded = open; door?.setAttribute("aria-expanded", String(open)); wrap?.classList.toggle("open", open); wrap?.toggleAttribute("inert", !open); }
  const quiet = (text: string, action?: string, busy = false) => {
    const q = h("div", "quiet"), d = h("div"); q.append(icon(INFO, 16), d); d.append(doc.createTextNode(text));
    if (action) { const b = button("", busy ? "Checking…" : action, () => void session.refresh()); b.disabled = busy; d.append(b); }
    d.append(outLink(`Open on ${siteName(host.link)} ↗`, host.link)); el.append(q);
  };
  function render() {
    if (disposed) return;
    const s = session.snapshot(), page = s.window;
    const state = discussionState(s);
    const nextShape = s.phase === "idle" || s.phase === "loading" ? s.phase : state;
    el.dataset.state = nextShape; el.hidden = nextShape === "idle" || nextShape === "none";
    if (nextShape !== shape || nextShape !== "ok") {
      el.replaceChildren(); nodes.clear(); shape = nextShape; door = null; inner = null;
      if (el.hidden) return;
      if (nextShape === "loading") { const row = h("div", "door wait"); row.append(h("span", "d-t", "Discussion")); const dots = h("span", "fetching"); dots.setAttribute("role", "status"); dots.setAttribute("aria-label", "Loading the discussion"); dots.append(h("i"), h("i"), h("i")); row.append(dots); el.append(row); return; }
      if (state !== "ok") {
        let text = state === "empty" ? "No comments yet" : state === "closed" ? "Comments are closed" : state === "waiting" ? "Waiting for a phone copy. Open this article in the phone app to fetch its discussion." : state === "restricted" ? "Read this discussion on the publisher's site. Sign-in may be required." : state === "blocked" ? `${siteName(host.link)} doesn't let readers fetch its comments.` : "The discussion couldn't be loaded.";
        const access = discussionPublisher(host.link)?.access;
        if (state === "restricted" && access === "subscriber") text = "This source keeps its discussion on its subscriber site. Open the original article to continue.";
        if (state === "restricted" && access === "account") text = "Sign in on the publisher's site to read reviews and comments.";
        if (s.stale && page && page.state !== "none") text += ` · Saved copy from ${since(page.fetchedAt, now())}. Couldn't check for updates.`;
        quiet(text, state === "waiting" || page?.phoneCopy ? "Check for newer copy" : state === "failed" || s.stale ? "Try again" : state === "empty" || state === "closed" ? "Refresh" : undefined, s.busy);
        return;
      }
      door = button("door", "", () => setOpen(!expanded)); door.setAttribute("aria-controls", id);
      wrap = h("div", "thread-wrap"); wrap.id = id; const clip = h("div", "thread"); inner = h("div", "thread-in"); clip.append(inner); wrap.append(clip);
      more = button("more", "", async () => {
        if (session.snapshot().failure === "changed") { await session.refresh(); return; }
        const before = new Set(session.snapshot().threads.map(t => t.id));
        await session.more();
        const arrived = [...inner!.querySelectorAll<HTMLElement>(".cm.root")].find(n => !before.has(n.dataset.id!));
        if (arrived) { arrived.tabIndex = -1; arrived.focus({ preventScroll: true }); }
      });
      foot = h("div", "d-foot"); stamp = h("span"); refresh = button("", "Refresh", () => void session.refresh()); foot.append(stamp, refresh, h("span", "sp"), outLink(`Comment on ${siteName(page!.link)} ↗`, page!.link));
      inner.append(more, foot); el.append(door, wrap); setOpen(expanded);
    }
    if (!page || !inner || !door || !more || !foot || !stamp || !refresh) return;
    if (s.more && !more.isConnected) inner.insertBefore(more, foot);
    const scroller = el.closest<HTMLElement>("[data-reader]"), top = scroller?.getBoundingClientRect().top ?? 0;
    const visibleAnchor = [...inner.querySelectorAll<HTMLElement>(".cm")].find(n => n.getBoundingClientRect().bottom > top);
    const anchorId = visibleAnchor?.dataset.id, anchorTop = visibleAnchor?.getBoundingClientRect().top;
    const focused = doc.activeElement instanceof HTMLElement && el.contains(doc.activeElement) ? doc.activeElement : null;
    const focusedComment = focused?.closest<HTMLElement>(".cm")?.dataset.id;
    const focusedReply = focused?.dataset.replies;
    const focusedText = focused?.textContent;
    const ids = new Set(s.threads.map(t => t.id));
    for (const [key, node] of nodes) if (!ids.has(key)) { node.el.remove(); nodes.delete(key); }
    s.threads.forEach((thread, i) => {
      const key = JSON.stringify([thread, session.record(thread.id)?.replyNext, s.replyBusy.has(thread.id), s.replyFailed.has(thread.id)]);
      let node = nodes.get(thread.id);
      if (node?.key !== key) { const next = comment(thread); if (node) node.el.replaceWith(next); node = { key, el: next }; nodes.set(thread.id, node); }
      const at = inner!.children[i]; if (at !== node.el) inner!.insertBefore(node.el, at ?? more);
    });
    door.replaceChildren(h("span", "d-t", "Discussion"), h("span", "d-n", `${page.total === undefined ? "Comments" : commentsLabel(page.total)} on ${siteName(page.link)}`), svgIcon(doc, CHEVRON, 18, "d-chev"));
    if (!s.more) more.remove();
    more.disabled = s.busy || s.replyBusy.size > 0;
    for (const control of inner.querySelectorAll<HTMLButtonElement>("[data-replies]")) control.disabled = s.busy || s.replyBusy.size > 0;
    more.textContent = s.failure === "changed" && s.moreFailed ? "Discussion changed. Refresh to continue" : s.moreFailed ? "Couldn't load more. Try again" : s.busy ? "Loading…" : "Show more conversations";
    stamp.textContent = s.failure === "changed" ? "The discussion changed. Refresh for the current copy." : s.stale ? `Saved copy from ${since(page.fetchedAt, now())}` : page.phoneCopy ? `Phone copy from ${since(page.fetchedAt, now())}` : `Updated ${since(page.fetchedAt, now())}`;
    if (page.partial && !s.more && s.threads.every(root => !session.record(root.id)?.replyNext)) stamp.textContent += " · Partial copy. Open the publisher's discussion for more.";
    refresh.textContent = page.phoneCopy ? "Check for newer copy" : s.stale ? "Try again" : "Refresh";
    refresh.disabled = s.busy || s.replyBusy.size > 0;
    if (anchorId && anchorTop !== undefined) {
      const target = [...inner.querySelectorAll<HTMLElement>(".cm")].find(n => n.dataset.id === anchorId);
      if (target) { const delta = target.getBoundingClientRect().top - anchorTop; const scroller = el.closest<HTMLElement>("[data-reader]"); if (scroller) scroller.scrollTop += delta; else if (delta) doc.defaultView?.scrollBy(0, delta); }
    }
    if (focused && !focused.isConnected) {
      const root = [...inner.querySelectorAll<HTMLElement>(".cm")].find(n => n.dataset.id === focusedComment);
      const replacement = root && [...root.querySelectorAll<HTMLButtonElement>("button")].find(b => focusedReply ? b.dataset.replies === focusedReply : b.textContent === focusedText);
      (replacement ?? root ?? more).focus({ preventScroll: true });
    }
  }
  render();
  return { start: () => session.start(), refresh: () => { void session.refresh(); }, pause: () => session.pause(), disable: () => session.disable(), expand: () => setOpen(true), info, dispose() { disposed = true; session.dispose(); el.replaceChildren(); nodes.clear(); } };
}
